import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Zap, Play, Pause, Copy, Trash2, PlayCircle, CheckCircle2, AlertTriangle, PlusCircle, X, TestTube, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { automationApi, mortgageApi } from './workspacesApi';
import { useTabParam } from './shared/useTabParam';
import NumberInput from './shared/NumberInput';
import { labelFor } from './shared/labels';
import { formatDateTime } from './kit/time';
import { useStaff } from './kit/OwnerChip';
import ActionParams, { actionsProblem, toApiActions, withoutScratch } from './automations/ActionParams';

const TRIGGERS = [
  'contact.created', 'lead.created', 'stage.changed',
  'application.started', 'application.completed',
  'document.uploaded', 'task.overdue',
  'email.opened', 'link.clicked', 'sms.received', 'call.missed',
  'appointment.scheduled', 'appointment.missed',
  'contract.executed', 'deadline.approaching',
  'rate_lock.expiring', 'closing.completed',
  'birthday', 'home_anniversary', 'custom_date', 'webhook.received',
];
const CONDITION_OPS = ['equals', 'not_equals', 'contains', 'in', 'not_in', 'greater_than', 'less_than', 'is_empty', 'is_not_empty', 'changed_from', 'changed_to'];
// Suggestions for the condition field box; any record key is still allowed.
const CONDITION_FIELDS = ['contact_type', 'lead_status', 'stage', 'loan_purpose', 'loan_type', 'loan_amount', 'referral_source', 'preferred_language'];
const NO_VALUE_OPS = ['is_empty', 'is_not_empty'];
const LIST_OPS = ['in', 'not_in'];
const NUMBER_OPS = ['greater_than', 'less_than'];

// The builder keeps every value as text while typing; this turns it into what the engine reads.
export function toApiConditions(conditions) {
  return conditions.map(({ field, operator, value }) => {
    const c = { field: field.trim(), operator };
    if (NO_VALUE_OPS.includes(operator)) return { ...c, value: null };
    if (LIST_OPS.includes(operator)) {
      return { ...c, value: Array.isArray(value) ? value : String(value ?? '').split(',').map(v => v.trim()).filter(Boolean) };
    }
    if (NUMBER_OPS.includes(operator)) {
      const n = Number(String(value ?? '').replace(/,/g, ''));
      return { ...c, value: String(value ?? '').trim() !== '' && Number.isFinite(n) ? n : value };
    }
    return { ...c, value: value ?? '' };
  });
}

const toFormConditions = (conditions) => (conditions || []).map(c => ({
  field: c.field || '', operator: c.operator, value: Array.isArray(c.value) ? c.value.join(', ') : (c.value ?? ''),
}));

const BLANK_WORKFLOW = {
  name: '', description: '', trigger: 'contact.created', division: 'any',
  conditions: [], conditions_join: 'and', actions: [], active: true, tags: [],
};

// What each stored per-action result means to a person reading a run.
const STEP_STATUS = {
  executed: { label: 'Done', color: '#6BA82E' },
  executed_dry: { label: 'Not delivered', color: '#b45309' },
  failed: { label: 'Failed', color: '#ef4444' },
};
const DRY_STEP_NOTE = 'Email, text and the other connectors are not wired yet, so nothing was sent.';

function RunDetail({ run }) {
  const steps = run.action_results || [];
  return (
    <div className="border-t border-slate-100 px-3 py-2 text-xs space-y-1" data-testid={`run-detail-${run.run_id}`}>
      {steps.length === 0 && run.status !== 'waiting' && <div className="text-slate-400">No steps ran.</div>}
      {steps.map((s, i) => {
        const st = STEP_STATUS[s.status] || { label: s.status, color: '#64748b' };
        const note = s.status === 'executed_dry' ? DRY_STEP_NOTE : s.note;
        return (
          <div key={s.action_id || i} className="flex items-start gap-2" data-testid={`run-step-${run.run_id}-${i}`}>
            <span className="w-4 text-slate-400">{i + 1}.</span>
            <span className="font-medium text-slate-700 w-40 shrink-0">{labelFor(null, s.type)}</span>
            <span className="font-semibold uppercase tracking-wider text-[10px] pt-0.5 w-24 shrink-0" style={{ color: st.color }}>{st.label}</span>
            <span className="flex-1 min-w-0 text-slate-500">{note}</span>
            <span className="text-slate-400 shrink-0">{formatDateTime(s.at)}</span>
          </div>
        );
      })}
      {run.status === 'waiting' && (
        <div className="text-slate-500" data-testid={`run-waiting-${run.run_id}`}>
          Waiting — step {(run.next_action_index || 0) + 1} runs {formatDateTime(run.next_run_at)}.
        </div>
      )}
      {run.status !== 'failed' && run.error && <div className="text-slate-500">{run.error}</div>}
    </div>
  );
}

const ACTIONS = ['send_email', 'send_sms', 'create_task', 'assign_user', 'move_stage', 'update_field', 'add_tag', 'remove_tag', 'generate_document', 'notify_manager', 'start_sequence', 'call_webhook', 'audrey_draft', 'add_internal_note'];


export default function AutomationsPage() {
  const [tab, setTab] = useTabParam('workflows', { valid: ['workflows', 'runs'] });
  const [workflows, setWorkflows] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipeModal, setRecipeModal] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerWf, setComposerWf] = useState(BLANK_WORKFLOW);
  // Set while editing a saved workflow; null while building a new one.
  const [editing, setEditing] = useState(null);
  const [testPayload, setTestPayload] = useState('{"record": {"contact_type": "buyer"}}');
  const [testResult, setTestResult] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const [stages, setStages] = useState([]);
  const staff = useStaff();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, r, rn] = await Promise.all([
        automationApi.listWorkflows(),
        automationApi.listRecipes(),
        automationApi.listRuns({ limit: 30 }),
      ]);
      setWorkflows(w.items || []);
      setRecipes(r.items || []);
      setRuns(rn.items || []);
    } catch (e) {
      toast({ title: 'Load failed', description: String(e) });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // The deal-stage picker; a failure leaves it empty rather than breaking the page.
  useEffect(() => { mortgageApi.getStages().then((r) => setStages(r?.stages || [])).catch(() => {}); }, []);

  const applyRecipe = async (recipe_id) => {
    try {
      await automationApi.createFromRecipe(recipe_id);
      toast({ title: 'Workflow created from recipe' });
      setRecipeModal(false);
      load();
    } catch (e) { toast({ title: 'Failed', description: String(e) }); }
  };

  const togglePause = async (wf) => {
    try {
      if (wf.active) await automationApi.pause(wf.workflow_id);
      else await automationApi.activate(wf.workflow_id);
      load();
    } catch (e) { toast({ title: 'Failed' }); }
  };

  const openBuilder = () => { setEditing(null); setComposerWf(BLANK_WORKFLOW); setComposerOpen(true); };

  const openEditor = (wf) => {
    setEditing(wf);
    setComposerWf({
      name: wf.name || '', description: wf.description || '', trigger: wf.trigger, division: wf.division || 'any',
      conditions: toFormConditions(wf.conditions), conditions_join: wf.conditions_join || 'and',
      actions: (wf.actions || []).map(a => ({ ...a, params: a.params || {} })), active: wf.active, tags: wf.tags || [],
    });
    setComposerOpen(true);
  };

  // Saves the composer: a new workflow, or changes to the one being edited. `test` also runs the
  // dry-run payload against it. A new workflow saved for testing is created paused, so it never
  // runs for real before it is activated; an edited one keeps whatever state it was in.
  const saveWorkflow = async ({ test }) => {
    if (composerWf.actions.length === 0 || (!test && !composerWf.name)) {
      toast({ title: test ? 'Add at least one action' : 'Name + at least one action required' });
      return;
    }
    const problem = actionsProblem(composerWf.actions);
    if (problem) { toast({ title: 'Fix the actions first', description: problem }); return; }
    let payload = {};
    if (test) {
      try { payload = JSON.parse(testPayload); } catch { toast({ title: 'Invalid test payload' }); return; }
    }
    const body = { ...composerWf, conditions: toApiConditions(composerWf.conditions), actions: toApiActions(composerWf.actions) };
    try {
      let saved;
      if (editing) {
        const changes = { ...body };
        delete changes.active;
        // A trigger filter belongs to the trigger it was saved with.
        if (changes.trigger !== editing.trigger) changes.trigger_filter = null;
        saved = await automationApi.updateWorkflow(editing.workflow_id, changes);
      } else {
        saved = await automationApi.createWorkflow(test ? { ...body, active: false } : body);
      }
      if (test) {
        setTestResult(await automationApi.test(saved.workflow_id, payload));
        toast({ title: editing ? 'Saved and tested' : 'Test complete (workflow saved paused)' });
        if (!editing) setEditing(saved);
      } else {
        toast({ title: editing && !editing.active ? 'Changes saved (workflow is still paused)' : editing ? 'Changes saved' : 'Workflow saved' });
        setComposerOpen(false);
        setEditing(null);
        setComposerWf(BLANK_WORKFLOW);
      }
      load();
    } catch (e) { toast({ title: test ? 'Test failed' : 'Save failed', description: String(e) }); }
  };

  const duplicate = async (wf) => {
    try { await automationApi.duplicate(wf.workflow_id); load(); toast({ title: 'Duplicated (paused)' }); }
    catch { toast({ title: 'Failed' }); }
  };

  const del = async (wf) => {
    if (!(await confirmDialog({ title: 'Delete this workflow?', description: 'Past runs will be preserved.', confirmText: 'Delete', destructive: true }))) return;
    try { await automationApi.deleteWorkflow(wf.workflow_id); load(); }
    catch { toast({ title: 'Failed' }); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="automations-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Zap style={{ color: '#070B5F' }} size={28} />
            <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }} data-testid="automations-title">Automations</h1>
          </div>
          <p className="text-slate-500 mt-1" data-testid="automations-subtitle">Triggers · conditions · actions — no-code workflow builder</p>
          {/* Ticket 209: CRM actions (task, note, tag, stage, assignment, field) are real;
              email, SMS and the other connectors are not wired yet and record a dry run. */}
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="automations-preview-banner">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span><b>Partly live.</b> Tasks, notes, tags, stage moves, assignments, and field updates really change your CRM. Emails, texts, and the other actions are not delivered yet — a run with one of those steps shows as a dry run.</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openBuilder} data-testid="composer-btn" className="btn-gold">
            <Zap size={16} /> Build from Scratch
          </button>
          <button onClick={() => setRecipeModal(true)} data-testid="use-recipe-btn" className="btn-navy">
            <Plus size={16} /> Use Recipe
          </button>
        </div>
      </div>

      <div className="border-b border-slate-100 flex gap-1">
        {[
          { k: 'workflows', l: 'Workflows', c: workflows.length },
          { k: 'runs', l: 'Recent Runs', c: runs.length },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`px-4 py-2 text-sm border-b-2 font-medium ${tab === t.k ? '' : 'border-transparent text-slate-500'}`}
            style={tab === t.k ? { borderColor: '#070B5F', color: '#070B5F' } : undefined}>
            {t.l} <span className="text-xs text-slate-400 ml-1">{t.c}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-16 flex items-center justify-center text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} /> Loading...
        </div>
      ) : tab === 'workflows' ? (
        workflows.length === 0 ? (
          <div className="p-16 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl" data-testid="workflows-empty">
            <Zap size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No workflows yet</p>
            <p className="text-sm">Click "Use Recipe" to spin up your first automation.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {workflows.map(wf => (
              <li key={wf.workflow_id} className="bg-white border border-slate-100 rounded-2xl shadow-sm p-4 flex items-center justify-between"
                data-testid={`workflow-${wf.workflow_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider"
                      style={wf.active ? { background: '#6BA82E20', color: '#6BA82E' } : { background: '#f1f5f9', color: '#64748b' }}
                    >
                      {wf.active ? 'Active' : 'Paused'}
                    </span>
                    <div className="text-sm font-medium truncate" style={{ color: '#070B5F' }}>{wf.name}</div>
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-slate-100 rounded px-1.5">{wf.trigger}</span>
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">{wf.division}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {wf.actions?.length || 0} action{(wf.actions?.length || 0) === 1 ? '' : 's'} ·
                    {' '}{wf.total_runs || 0} runs · {wf.successful_runs || 0} succeeded
                  </div>
                </div>
                <div className="flex items-center">
                  <button onClick={() => togglePause(wf)} title={wf.active ? 'Pause' : 'Activate'}
                    className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`toggle-${wf.workflow_id}`}>
                    {wf.active ? <Pause size={14} style={{ color: '#070B5F' }} /> : <Play size={14} style={{ color: '#070B5F' }} />}
                  </button>
                  <button onClick={() => openEditor(wf)} title="Edit" className="p-2 hover:bg-slate-100 rounded-lg ml-1" data-testid={`edit-${wf.workflow_id}`}>
                    <Pencil size={14} style={{ color: '#070B5F' }} />
                  </button>
                  <button onClick={() => duplicate(wf)} title="Duplicate" className="p-2 hover:bg-slate-100 rounded-lg ml-1">
                    <Copy size={14} style={{ color: '#070B5F' }} />
                  </button>
                  <button onClick={() => del(wf)} title="Delete" className="p-2 hover:bg-slate-100 rounded-lg ml-1">
                    <Trash2 size={14} className="text-red-500" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : (
        runs.length === 0 ? (
          <div className="p-16 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">No runs yet</div>
        ) : (
          <ul className="space-y-2">
            {runs.map(r => {
              const open = expandedRun === r.run_id;
              return (
              <li key={r.run_id} className="bg-white border border-slate-100 rounded-xl shadow-sm text-sm" data-testid={`run-${r.run_id}`}>
                <button type="button" onClick={() => setExpandedRun(open ? null : r.run_id)} aria-expanded={open}
                  data-testid={`run-toggle-${r.run_id}`} className="w-full p-3 flex items-center gap-3 text-left">
                  {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                  {r.status === 'succeeded' ? <CheckCircle2 style={{ color: '#6BA82E' }} size={16} /> :
                   r.status === 'failed' ? <AlertTriangle className="text-red-500" size={16} /> :
                   r.status === 'dry_run' ? <AlertTriangle className="text-amber-500" size={16} /> :
                   <PlayCircle style={{ color: '#070B5F' }} size={16} />}
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-600 truncate">{r.trigger}</div>
                    <div className="text-[11px] text-slate-500">{formatDateTime(r.started_at)}</div>
                    {r.status === 'failed' && r.error && <div className="text-[11px] text-red-500 truncate" data-testid={`run-error-${r.run_id}`}>{r.error}</div>}
                  </div>
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider"
                    style={
                      r.status === 'succeeded' ? { background: '#6BA82E20', color: '#6BA82E' } :
                      r.status === 'failed' ? { background: '#fef2f2', color: '#ef4444' } :
                      r.status === 'dry_run' ? { background: '#fffbeb', color: '#b45309' } :
                      { background: '#f1f5f9', color: '#64748b' }
                    }
                    title={r.status === 'dry_run' ? 'At least one step (email, text, …) is not delivered yet' : r.error || undefined}
                  >
                    {r.status === 'dry_run' ? 'dry run' : r.status}
                  </span>
                </button>
                {open && <RunDetail run={r} />}
              </li>
              );
            })}
          </ul>
        )
      )}

      {/* Composer */}
      <Dialog open={composerOpen} onOpenChange={(v) => { setComposerOpen(v); if (!v) setTestResult(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="composer-modal">
          <DialogHeader><DialogTitle className="font-serif" style={{ color: '#070B5F' }} data-testid="composer-title">{editing ? 'Edit Workflow' : 'Build Workflow'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={composerWf.name} onChange={(e) => setComposerWf({ ...composerWf, name: e.target.value })} className="mt-1" data-testid="composer-name" />
              </div>
              <div>
                <Label>Division</Label>
                <Select value={composerWf.division} onValueChange={(v) => setComposerWf({ ...composerWf, division: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mortgage">Mortgage</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="any">Any</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={composerWf.description} onChange={(e) => setComposerWf({ ...composerWf, description: e.target.value })} rows={2} className="mt-1" />
              </div>
              <div>
                <Label>Trigger</Label>
                <Select value={composerWf.trigger} onValueChange={(v) => setComposerWf({ ...composerWf, trigger: v })}>
                  <SelectTrigger className="mt-1" data-testid="composer-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRIGGERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Conditions Match</Label>
                <Select value={composerWf.conditions_join} onValueChange={(v) => setComposerWf({ ...composerWf, conditions_join: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="and">All (AND)</SelectItem>
                    <SelectItem value="or">Any (OR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Conditions</Label>
                <button onClick={() => setComposerWf({ ...composerWf, conditions: [...composerWf.conditions, { field: '', operator: 'equals', value: '' }] })}
                  data-testid="add-condition"
                  className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><PlusCircle size={12} /> Add</button>
              </div>
              {composerWf.conditions.length === 0 && <p className="text-xs text-slate-400">Runs for every trigger event (no conditions).</p>}
              {composerWf.conditions.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100">
                  <datalist id="condition-fields">{CONDITION_FIELDS.map(f => <option key={f} value={f} />)}</datalist>
                  {composerWf.conditions.map((c, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 p-3 items-center">
                      <Input placeholder="field" list="condition-fields" value={c.field} data-testid={`condition-field-${i}`} onChange={(e) => {
                        const arr = [...composerWf.conditions]; arr[i] = { ...arr[i], field: e.target.value };
                        setComposerWf({ ...composerWf, conditions: arr });
                      }} />
                      <Select value={c.operator} onValueChange={(v) => {
                        const arr = [...composerWf.conditions]; arr[i] = { ...arr[i], operator: v };
                        setComposerWf({ ...composerWf, conditions: arr });
                      }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{CONDITION_OPS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                      {NO_VALUE_OPS.includes(c.operator) ? (
                        <span className="text-xs text-slate-400" data-testid={`condition-novalue-${i}`}>no value needed</span>
                      ) : NUMBER_OPS.includes(c.operator) ? (
                        <NumberInput
                          allowNegative placeholder="number" value={c.value === '' ? null : c.value}
                          data-testid={`condition-value-${i}`}
                          onChange={(n) => {
                            const arr = [...composerWf.conditions]; arr[i] = { ...arr[i], value: n ?? '' };
                            setComposerWf({ ...composerWf, conditions: arr });
                          }} />
                      ) : (
                        <Input
                          placeholder={LIST_OPS.includes(c.operator) ? 'a, b, c' : 'value'}
                          value={c.value}
                          data-testid={`condition-value-${i}`}
                          onChange={(e) => {
                            const arr = [...composerWf.conditions]; arr[i] = { ...arr[i], value: e.target.value };
                            setComposerWf({ ...composerWf, conditions: arr });
                          }} />
                      )}
                      <button onClick={() => {
                        const arr = composerWf.conditions.filter((_, idx) => idx !== i);
                        setComposerWf({ ...composerWf, conditions: arr });
                      }} className="p-2 hover:bg-slate-100 rounded-lg"><X size={14} className="text-red-500" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Actions</Label>
                <button onClick={() => setComposerWf({ ...composerWf, actions: [...composerWf.actions, { type: 'send_email', delay_minutes: 0, params: {} }] })}
                  data-testid="add-action"
                  className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><PlusCircle size={12} /> Add</button>
              </div>
              {composerWf.actions.length === 0 && <p className="text-xs text-red-500">At least one action is required.</p>}
              {composerWf.actions.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100">
                  {composerWf.actions.map((a, i) => {
                    const patch = (changes) => {
                      const arr = [...composerWf.actions]; arr[i] = { ...arr[i], ...changes };
                      setComposerWf({ ...composerWf, actions: arr });
                    };
                    return (
                    <div key={i} className="p-3 space-y-2" data-testid={`action-row-${i}`}>
                      <div className="grid grid-cols-[1.5fr_1fr_auto] gap-2 items-center">
                        <Select value={a.type} onValueChange={(v) => {
                          // Another kind of step takes other params, so the old ones go.
                          const arr = [...composerWf.actions]; arr[i] = { ...withoutScratch(arr[i]), type: v, params: {} };
                          setComposerWf({ ...composerWf, actions: arr });
                        }}>
                          <SelectTrigger data-testid={`action-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ACTIONS.map(o => <SelectItem key={o} value={o}>{labelFor(null, o)}</SelectItem>)}</SelectContent>
                        </Select>
                        <NumberInput integer placeholder="Delay (min)" value={a.delay_minutes} onChange={(n) => patch({ delay_minutes: n ?? 0 })} />
                        <button onClick={() => {
                          const arr = composerWf.actions.filter((_, idx) => idx !== i);
                          setComposerWf({ ...composerWf, actions: arr });
                        }} className="p-2 hover:bg-slate-100 rounded-lg"><X size={14} className="text-red-500" /></button>
                      </div>
                      <ActionParams action={a} index={i} staff={staff} stages={stages} onChange={patch} />
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Test payload */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Dry-run Test Payload (JSON)</Label>
              </div>
              <Textarea value={testPayload} onChange={(e) => setTestPayload(e.target.value)} rows={3} className="text-xs font-mono" data-testid="test-payload" />
              {testResult && (
                <div className="mt-2 text-xs">
                  <div style={{ color: testResult.would_run ? '#6BA82E' : '#ef4444' }}>
                    would_run: {String(testResult.would_run)}
                  </div>
                  <div className="text-slate-500 mt-1">plan: {testResult.plan?.length || 0} action(s)</div>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <button onClick={() => saveWorkflow({ test: true })} data-testid="composer-test-btn"
                className="btn-gold">
                <TestTube size={16} /> Save + Test
              </button>
              <div className="flex gap-2">
                <button onClick={() => setComposerOpen(false)} className="btn-outline">Cancel</button>
                <button onClick={() => saveWorkflow({ test: false })} data-testid="composer-save-btn"
                  className="btn-navy">{editing ? 'Save Changes' : 'Save Workflow'}</button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipes */}
      <Dialog open={recipeModal} onOpenChange={setRecipeModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif" style={{ color: '#070B5F' }}>Automation Recipes</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Pre-built workflows for mortgage & real estate. Click to add.</p>
          <ul className="divide-y divide-slate-100">
            {recipes.map(r => (
              <li key={r.recipe_id} className="py-3 flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: '#070B5F' }}>{r.name}</div>
                  <div className="text-xs text-slate-500">{r.description}</div>
                  <div className="text-[10px] uppercase text-slate-400 mt-1">{r.division} · trigger: {r.trigger}</div>
                </div>
                <button onClick={() => applyRecipe(r.recipe_id)}
                  data-testid={`recipe-${r.recipe_id}`}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-full text-white"
                  style={{ background: '#070B5F' }}>
                  Add
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
